import { useState, useCallback, useRef, useEffect } from 'react'
import type { FunnelConfig } from '@/lib/hooks/useFunnelConfig'
import { generateMultiCombinations, type Combination } from './combinationGenerator'
import type { SwipeDirection } from './SwipeCard'

const INITIAL_BATCH = 20
const REFILL_THRESHOLD = 5
const REFILL_BATCH = 10
const MAX_LIKES = 50

export interface StoredLike {
  key: string
  funnelId: string
  funnelSlug: string
  baseImage: string
  wcIds: number[]
  totalPrice: number
  timestamp: number
  products: Array<{
    categoryId: string
    productId: string
    productImage: string
    productLayer: number
    productName: string
  }>
}

function likesStorageKey(slugs: string[]) {
  return `swiper_likes_${slugs.sort().join('+')}`
}

function loadLikes(slugs: string[]): StoredLike[] {
  try {
    const raw = localStorage.getItem(likesStorageKey(slugs))
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

function saveLikes(slugs: string[], likes: StoredLike[]) {
  localStorage.setItem(likesStorageKey(slugs), JSON.stringify(likes.slice(0, MAX_LIKES)))
}

function combinationToStoredLike(combo: Combination): StoredLike {
  return {
    key: combo.key,
    funnelId: combo.funnelId,
    funnelSlug: combo.funnelSlug,
    baseImage: combo.baseImage,
    wcIds: combo.wcIds,
    totalPrice: combo.totalPrice,
    timestamp: Date.now(),
    products: combo.products.map(p => ({
      categoryId: p.categoryId,
      productId: p.product.id,
      productImage: p.product.image,
      productLayer: p.product.layer,
      productName: p.product.name,
    })),
  }
}

export function useSwiperState(configs: FunnelConfig[]) {
  const [queue, setQueue] = useState<Combination[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [likes, setLikes] = useState<StoredLike[]>([])
  const [isFinished, setIsFinished] = useState(false)
  const seenKeysRef = useRef(new Set<string>())
  const initializedRef = useRef(false)

  const slugs = configs.map(c => c.slug)

  useEffect(() => {
    if (configs.length === 0 || initializedRef.current) return
    initializedRef.current = true

    const stored = loadLikes(slugs)
    setLikes(stored)

    const combos = generateMultiCombinations(configs, INITIAL_BATCH, seenKeysRef.current)
    setQueue(combos)
    if (combos.length === 0) setIsFinished(true)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configs.length])

  const maybeRefill = useCallback(() => {
    if (configs.length === 0) return

    const remaining = queue.length - currentIndex - 1
    if (remaining <= REFILL_THRESHOLD) {
      const more = generateMultiCombinations(configs, REFILL_BATCH, seenKeysRef.current)
      if (more.length > 0) {
        setQueue(prev => [...prev, ...more])
      } else if (remaining <= 0) {
        setIsFinished(true)
      }
    }
  }, [configs, queue.length, currentIndex])

  const currentCombination = queue[currentIndex] ?? null

  const advance = useCallback(() => {
    setCurrentIndex(prev => prev + 1)
    setTimeout(maybeRefill, 0)
  }, [maybeRefill])

  const handleSwipe = useCallback((direction: SwipeDirection): 'like' | 'dislike' | 'superlike' => {
    const combo = currentCombination
    if (!combo) return 'dislike'

    if (direction === 'right') {
      const stored = combinationToStoredLike(combo)
      setLikes(prev => {
        const updated = [stored, ...prev].slice(0, MAX_LIKES)
        saveLikes(slugs, updated)
        return updated
      })
      advance()
      return 'like'
    }

    if (direction === 'up') {
      advance()
      return 'superlike'
    }

    advance()
    return 'dislike'
  }, [currentCombination, slugs, advance])

  const removeLike = useCallback((key: string) => {
    setLikes(prev => {
      const updated = prev.filter(l => l.key !== key)
      saveLikes(slugs, updated)
      return updated
    })
  }, [slugs])

  const restart = useCallback(() => {
    if (configs.length === 0) return
    seenKeysRef.current.clear()
    setCurrentIndex(0)
    setIsFinished(false)
    const combos = generateMultiCombinations(configs, INITIAL_BATCH, seenKeysRef.current)
    setQueue(combos)
    if (combos.length === 0) setIsFinished(true)
  }, [configs])

  return {
    currentCombination,
    likes,
    likesCount: likes.length,
    isFinished,
    handleSwipe,
    removeLike,
    restart,
  }
}
